import { useEffect } from 'react';
import { getDefaultStyleNonce } from '@sas/nova-core/util';
import { GlobalSettingsState } from '../GlobalSettingsProvider';
import { usePreviousValue } from '../../../hooks/usePreviousValue';
import { useSetTheme } from '../../AppRoot/ThemeStateProvider';
import {
  ThemeIdMap,
  isSystemApplicationThemeId,
  LightThemeParameters,
  DarkThemeParameters,
  HighContrastThemeParameters,
  BaseThemeName,
  ThemeParameters,
} from '../../../design-tokens';
import { useGlobalSettingsThemeParametersDispatch } from '../../../theme';
import { GlobalSettingsKeys } from '../GlobalSettingsKeys';

export function useApplyGlobalSettingsChange(currentState: GlobalSettingsState) {
  const previousState = usePreviousValue(currentState);
  const setTheme = useSetTheme()!;
  const globalSettingsThemeParamsDispatch = useGlobalSettingsThemeParametersDispatch();
  const themeParameters: { [key in BaseThemeName]: ThemeParameters } = {
    light: LightThemeParameters,
    dark: DarkThemeParameters,
    'high-contrast': HighContrastThemeParameters,
  };

  useEffect(() => {
    function addInvertStyleBlock() {
      let $style = document.getElementById('sasSettingsInvertStyle');
      if ($style === null) {
        const nonce = getDefaultStyleNonce();
        $style = document.createElement('style');
        $style.setAttribute('id', 'sasSettingsInvertStyle');
        if (nonce) {
          $style.setAttribute('nonce', nonce);
        }
        document.querySelector('head')!.appendChild($style);
        $style.textContent = `html, body > iframe {-webkit-filter: invert(100%);-moz-filter: invert(100%);-o-filter: invert(100%);-ms-filter: invert(100%);filter: invert(100%);}`;
      }
    }

    function removeInvertStyleBlock() {
      const $style = document.getElementById('sasSettingsInvertStyle');
      if ($style !== null) {
        document.querySelector('head')!.removeChild($style);
      }
    }

    if (previousState !== undefined) {
      let focusStyleEvaluated = false;

      for (const key in GlobalSettingsKeys) {
        const id = GlobalSettingsKeys[key as keyof typeof GlobalSettingsKeys];
        const value = currentState[id];
        const previousValue = previousState[id];

        if (value !== previousValue) {
          switch (id) {
            case GlobalSettingsKeys.THEMES_KEY:
              if (value) {
                setTheme &&
                  setTheme(
                    isSystemApplicationThemeId(value)
                      ? {
                          type: 'application',
                          theme: {
                            ...themeParameters[ThemeIdMap[value].baseThemeName as BaseThemeName],
                            enableNovaTokens: true,
                          },
                          inverseTheme: {
                            ...themeParameters[ThemeIdMap[value].baseThemeName as BaseThemeName],
                            enableNovaTokens: true,
                          },
                        }
                      : {
                          type: 'request',
                          themeId: value,
                        }
                  );
              }
              break;

            case GlobalSettingsKeys.FOCUS_OVERRIDE:
            case GlobalSettingsKeys.FOCUS_COLOR:
            case GlobalSettingsKeys.FOCUS_OPACITY:
            case GlobalSettingsKeys.FOCUS_THICKNESS:
              if (!focusStyleEvaluated) {
                focusStyleEvaluated = true;

                const focusOverride =
                  currentState[GlobalSettingsKeys.FOCUS_OVERRIDE] === 'true' ? true : false;

                globalSettingsThemeParamsDispatch.enableCustomFocus(focusOverride);

                if (focusOverride) {
                  const customFocusColor = currentState[GlobalSettingsKeys.FOCUS_COLOR];
                  const customFocusThickness = currentState[GlobalSettingsKeys.FOCUS_THICKNESS];
                  const customFocusOpacity = currentState[GlobalSettingsKeys.FOCUS_OPACITY];

                  if (customFocusColor) {
                    globalSettingsThemeParamsDispatch.setCustomFocusColor(customFocusColor);
                  } else {
                    globalSettingsThemeParamsDispatch.removeCustomFocusColor();
                  }

                  if (customFocusThickness) {
                    globalSettingsThemeParamsDispatch.setCustomFocusThickness(
                      customFocusThickness.concat('px')
                    );
                  } else {
                    globalSettingsThemeParamsDispatch.removeCustomFocusThickness();
                  }

                  if (customFocusOpacity) {
                    globalSettingsThemeParamsDispatch.setCustomFocusOpacity(customFocusOpacity);
                  } else {
                    globalSettingsThemeParamsDispatch.removeCustomFocusOpacity();
                  }
                } else {
                  globalSettingsThemeParamsDispatch.removeCustomFocusColor();
                  globalSettingsThemeParamsDispatch.removeCustomFocusThickness();
                  globalSettingsThemeParamsDispatch.removeCustomFocusOpacity();
                }
              }
              break;

            case GlobalSettingsKeys.PLAY_VISUAL_EFFECTS:
              globalSettingsThemeParamsDispatch.enableVisualEffects(value === 'true');
              break;

            case GlobalSettingsKeys.INVERT_APP_COLORS:
              if (value === 'true') {
                addInvertStyleBlock();
              } else {
                removeInvertStyleBlock();
              }
              break;
          }
        }
      }
    }
  });
}
